/* Noire Atelier — product.js */
(function () {
  'use strict';

  /* ---------- Product form (AJAX add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    onSubmit(e) {
      e.preventDefault();
      const submitBtn = e.submitter && e.submitter.name === 'add'
        ? e.submitter
        : this.form.querySelector('[data-atc-button]') || this.form.querySelector('[type="submit"]');
      if (submitBtn && submitBtn.disabled) return;
      if (this.error) this.error.hidden = true;
      const busyTargets = [submitBtn];
      const stickyBtn = document.querySelector('[data-sticky-atc-button][form="' + this.form.id + '"]');
      if (stickyBtn) busyTargets.push(stickyBtn);
      busyTargets.forEach((b) => { if (b) { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); } });
      const formData = new FormData(this.form);
      window.NoireCart.addFormData(formData)
        .then(() => {
          window.NoireUtils.announce(window.a11yStrings.itemAdded);
          const quickViewModal = this.closest('#QuickViewModal')?.querySelector('theme-modal');
          if (quickViewModal?.close) quickViewModal.close();
          if (window.themeSettings.cartType === 'drawer') {
            window.NoireCart.openDrawer();
          }
        })
        .catch((err) => {
          if (this.error) {
            this.error.hidden = false;
            this.error.textContent = (err && (err.description || err.message)) || window.a11yStrings.cartError;
          }
        })
        .finally(() => {
          busyTargets.forEach((b) => { if (b) { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); } });
        });
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Variant selects ---------- */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const dataScript = this.querySelector('[data-variant-json]');
      if (!dataScript) return;
      try { this.variants = JSON.parse(dataScript.textContent); } catch (err) { this.variants = []; }
      this.root = this.closest('[data-product-root]');
      this.url = this.dataset.url;
      this.sectionId = this.dataset.section;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
      const initialId = this.root && this.root.querySelector('input[name="id"]');
      const initialVariant = initialId && this.variants.find((v) => String(v.id) === initialId.value);
      const initialGallery = this.root && this.root.querySelector('media-gallery');
      if (initialVariant && initialGallery && initialGallery.setVariantMedia) initialGallery.setVariantMedia(initialVariant, this.variants);
      if (this.closest('.nas-cart-quick-view')) this.updateCartQuickViewMedia();
    }
    get selectedOptions() {
      const opts = [];
      this.querySelectorAll('fieldset[data-option-index]').forEach((set) => {
        const checked = set.querySelector('input:checked');
        if (checked) opts[parseInt(set.dataset.optionIndex, 10)] = checked.value;
      });
      this.querySelectorAll('select[data-option-index]').forEach((sel) => {
        opts[parseInt(sel.dataset.optionIndex, 10)] = sel.value;
      });
      return opts;
    }
    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }
    onChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);
      this.markAvailability();
      this.querySelectorAll('fieldset[data-option-index]').forEach((set) => {
        const checked = set.querySelector('input:checked');
        const current = set.querySelector('.opt-current');
        if (checked && current) current.textContent = checked.value;
      });
      const forms = this.root ? this.root.querySelectorAll('input[name="id"]') : [];
      if (!variant) {
        this.setButtonState('unavailable');
        return;
      }
      forms.forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: false }));
      });
      this.setButtonState(variant.available ? 'add' : 'soldout');
      if (this.updateUrl && this.url) {
        const nextUrl = new URL(this.url, window.location.origin);
        nextUrl.searchParams.set('variant', variant.id);
        const currentView = new URL(window.location.href).searchParams.get('view');
        if (currentView) nextUrl.searchParams.set('view', currentView);
        window.history.replaceState({}, '', nextUrl.pathname + nextUrl.search);
      }
      this.renderSection(variant);
      if (this.closest('.nas-cart-quick-view')) this.updateCartQuickViewMedia();
      const gallery = this.root && this.root.querySelector('media-gallery');
      if (gallery && gallery.setVariantMedia) gallery.setVariantMedia(variant, this.variants);
    }
    updateCartQuickViewMedia() {
      const colourSelect = Array.from(this.querySelectorAll('select[data-option-index]')).find((select) => {
        const label = select.closest('.field')?.querySelector('label')?.textContent || '';
        return /^colou?r$/i.test(label.trim());
      });
      if (colourSelect && this.root) updateCartQuickViewImages(this.root, colourSelect.value);
    }
    setButtonState(state) {
      if (!this.root) return;
      this.root.querySelectorAll('[data-atc-button]').forEach((btn) => {
        const textEl = btn.querySelector('[data-atc-text]') || btn;
        if (state === 'add') {
          btn.disabled = false;
          textEl.textContent = btn.dataset.textAdd;
        } else {
          btn.disabled = true;
          textEl.textContent = state === 'soldout' ? btn.dataset.textSoldout : btn.dataset.textUnavailable;
        }
      });
      this.mirrorSticky();
    }
    markAvailability() {
      const options = this.selectedOptions;
      this.querySelectorAll('fieldset[data-option-index]').forEach((set) => {
        const index = parseInt(set.dataset.optionIndex, 10);
        set.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = options.slice();
          candidate[index] = input.value;
          const match = this.findVariant(candidate);
          const unavailable = !match || !match.available;
          const wrap = input.closest('.opt-btn');
          if (wrap) wrap.classList.toggle('is-unavailable', unavailable);
        });
      });
    }
    renderSection(variant) {
      if (!this.url || !this.sectionId) return;
      let url = this.url + '?variant=' + variant.id + '&section_id=' + this.sectionId;
      const plan = this.root && this.root.querySelector('input[name="selling_plan"]:checked');
      if (plan && plan.value) url += '&selling_plan=' + plan.value;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      fetch(url, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const activeId = document.activeElement && document.activeElement.id;
          document.querySelectorAll('[data-swap]').forEach((el) => {
            const fresh = doc.getElementById(el.id);
            if (fresh) el.innerHTML = fresh.innerHTML;
          });
          if (activeId) {
            const restore = document.getElementById(activeId);
            if (restore && document.activeElement !== restore) restore.focus({ preventScroll: true });
          }
          const gallery = this.root && this.root.querySelector('media-gallery');
          // The section response exposes Shopify's variant featured_media, but
          // that media is not a reliable colour anchor across this catalogue.
          // Reapply the colour-media map after the async refresh instead of
          // letting a late response jump the gallery to another colour.
          if (gallery && gallery.setVariantMedia) {
            gallery.setVariantMedia(variant, this.variants);
          }
          this.mirrorSticky();
        })
        .catch(() => {});
    }
    mirrorSticky() {
      if (!this.root) return;
      const mainBtn = this.root.querySelector('.buy-buttons [data-atc-button]');
      const stickyBtn = this.root.querySelector('[data-sticky-atc-button]');
      if (mainBtn && stickyBtn) {
        stickyBtn.disabled = mainBtn.disabled;
        const src = mainBtn.querySelector('[data-atc-text]') || mainBtn;
        const dst = stickyBtn.querySelector('[data-atc-text]') || stickyBtn;
        dst.textContent = src.textContent;
      }
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  /* ---------- Media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('.gallery-main');
      this.slides = Array.from(this.querySelectorAll('.gallery-main .slide'));
      this.thumbs = Array.from(this.querySelectorAll('.gallery-thumbs button'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.layout = this.dataset.layout || 'stacked';
      if (!this.slides.length) return;
      this.index = 0;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.setActiveMedia(t.dataset.mediaId, true)));
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      if (this.main) {
        this.main.addEventListener('scroll', window.NoireUtils.debounce(() => this.syncFromScroll(), 100), { passive: true });
      }
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom) this.openZoom(zoom.dataset.zoom, zoom.dataset.zoomAlt || '');
      });
      this.select(0, false);
      window.setTimeout(() => {
        const root = this.closest('[data-product-root]');
        const variantData = root && root.querySelector('[data-variant-json]');
        const activeId = root && root.querySelector('input[name="id"]');
        if (!variantData || !activeId) return;
        try {
          const variants = JSON.parse(variantData.textContent);
          const active = variants.find((item) => String(item.id) === activeId.value);
          if (active) this.setVariantMedia(active, variants);
        } catch (error) {}
      }, 0);
    }
    setVariantMedia(variant, variants) {
      if (!variant) return;
      let colorMediaMap = {};
      const mapScript = this.closest('[data-product-root]') && this.closest('[data-product-root]').querySelector('[data-color-media-map]');
      if (mapScript) {
        try { colorMediaMap = JSON.parse(mapScript.textContent) || {}; } catch (error) {}
      }
      const colorIndexMarker = this.closest('[data-product-root]') && this.closest('[data-product-root]').querySelector('[data-color-option-index]');
      const colorOptionIndex = colorIndexMarker ? Number(colorIndexMarker.dataset.colorOptionIndex) : 0;
      const selectedColorOption = String((variant.options || [])[colorOptionIndex] || '').trim().toLowerCase();
      const mapKey = Object.keys(colorMediaMap).find((key) =>
        selectedColorOption === String(key).trim().toLowerCase()
      );
      if (mapKey && Array.isArray(colorMediaMap[mapKey]) && colorMediaMap[mapKey].length) {
        const mappedIds = new Set(colorMediaMap[mapKey].map(String));
        const mapped = this.slides.filter((slide) => mappedIds.has(String(slide.dataset.mediaId)));
        if (mapped.length) {
          this.showOnly(mapped);
          return;
        }
      }
      const selectedColor = selectedColorOption;
      const altMatched = this.slides.filter((slide) => {
        const match = String(slide.dataset.mediaAlt || '').match(/\[\s*color\s*:\s*([^\]]+)\]/i);
        return match && match[1].trim().toLowerCase() === selectedColor;
      });
      if (altMatched.length) {
        this.showOnly(altMatched);
        return;
      }
      if (!variant.featured_media_id) {
        this.showOnly(this.slides);
        return;
      }
      const anchorIndexes = Array.from(new Set((variants || [])
        .map((item) => this.slides.findIndex((slide) => slide.dataset.mediaId === String(item.featured_media_id)))
        .filter((index) => index >= 0))).sort((a, b) => a - b);
      const start = this.slides.findIndex((slide) => slide.dataset.mediaId === String(variant.featured_media_id));
      if (start < 0) return;
      const next = anchorIndexes.find((index) => index > start);
      const end = typeof next === 'number' ? next : this.slides.length;
      this.showOnly(this.slides.slice(start, end));
    }
    showOnly(visible) {
      const visibleSet = new Set(visible);
      const visibleIds = new Set(visible.map((slide) => String(slide.dataset.mediaId)));
      this.slides.forEach((slide) => { slide.hidden = !visibleSet.has(slide); });
      this.thumbs.forEach((thumb) => { thumb.hidden = !visibleIds.has(String(thumb.dataset.mediaId)); });
      this.visibleSlides = visible.slice();
      this.updateMosaicTail();
      const targetIndex = this.slides.indexOf(this.visibleSlides[0]);
      if (targetIndex >= 0) this.select(targetIndex, false);
    }
    updateMosaicTail() {
      this.slides.forEach((slide) => slide.classList.remove('is-mosaic-tail'));
      if (this.layout === 'mosaic' && this.visibleSlides && this.visibleSlides.length % 2 === 1) {
        this.visibleSlides[this.visibleSlides.length - 1].classList.add('is-mosaic-tail');
      }
    }
    setActiveMedia(mediaId, scroll) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.select(i, scroll !== false);
    }
    step(dir) {
      const visible = this.visibleSlides && this.visibleSlides.length ? this.visibleSlides : this.slides;
      const current = Math.max(0, visible.indexOf(this.slides[this.index]));
      const target = visible[(current + dir + visible.length) % visible.length];
      this.select(this.slides.indexOf(target), true);
    }
    select(i, scroll) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-selected', n === i ? 'true' : 'false'));
      if (this.counter) {
        const visible = this.visibleSlides && this.visibleSlides.length ? this.visibleSlides : this.slides;
        const visibleIndex = visible.indexOf(this.slides[i]);
        this.counter.textContent = (Math.max(0, visibleIndex) + 1) + ' / ' + visible.length;
      }
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      if (scroll && this.main) {
        if (!isDesktop || this.layout === 'carousel') {
          this.main.scrollTo({ left: this.slides[i].offsetLeft, behavior: window.NoireUtils.prefersReducedMotion() ? 'auto' : 'smooth' });
        } else if (this.layout === 'stacked') {
          this.slides[i].scrollIntoView({ behavior: window.NoireUtils.prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
        }
      }
    }
    syncFromScroll() {
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      if (isDesktop && this.layout !== 'carousel') return;
      const left = this.main.scrollLeft;
      let closest = 0;
      let min = Infinity;
      this.slides.forEach((s, n) => {
        if (s.hidden) return;
        const d = Math.abs(s.offsetLeft - left);
        if (d < min) { min = d; closest = n; }
      });
      if (closest !== this.index) this.select(closest, false);
    }
    openZoom(src, alt) {
      const modal = document.getElementById('MediaZoomModal');
      if (!modal) return;
      const target = modal.querySelector('[data-zoom-target]');
      if (target) {
        target.innerHTML = '';
        const img = document.createElement('img');
        img.src = src;
        img.alt = alt;
        target.appendChild(img);
      }
      const el = modal.matches('theme-modal') ? modal : modal.closest('theme-modal') || modal.querySelector('theme-modal');
      if (el && el.open) el.open();
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---------- Sticky ATC ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const targetId = this.dataset.observe;
      const target = targetId && document.getElementById(targetId);
      const bar = this.querySelector('.sticky-atc');
      if (!target || !bar || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { threshold: 0 });
      io.observe(target);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Quick view + quick add ---------- */
  function openQuickView(productUrl, trigger) {
    const shell = document.getElementById('QuickViewModal');
    if (!shell) { window.location.href = productUrl; return; }
    const overlay = shell.querySelector('theme-modal') || shell.closest('theme-modal');
    const target = shell.querySelector('[data-quick-view-target]');
    if (!overlay || !target) { window.location.href = productUrl; return; }
    const isCartQuickView = !!trigger?.closest('[data-recommendations-wrap]') || trigger?.hasAttribute('data-cart-quick-view');
    shell.classList.toggle('is-cart-quick-view', isCartQuickView);
    target.innerHTML = '<div class="quick-view__loading"><span class="spinner" role="status"><span class="visually-hidden">' + window.a11yStrings.loading + '</span></span></div>';
    overlay.open(trigger);
    const sep = productUrl.includes('?') ? '&' : '?';
    fetch(productUrl + sep + 'section_id=' + (isCartQuickView ? 'nas-cart-quick-view' : 'quick-view'))
      .then((r) => {
        if (!r.ok) throw new Error('load');
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('.quick-view__content');
        if (!content) throw new Error('empty');
        target.innerHTML = '';
        target.appendChild(content);
        const panel = overlay.querySelector('.modal__panel');
        if (panel) window.NoireUtils.trapFocus(panel);
      })
      .catch(() => {
        target.innerHTML = '<p><a class="link" href="' + productUrl + '">' + (target.dataset.fallbackText || 'View product') + '</a></p>';
      });
  }

  document.addEventListener('click', (e) => {
    const wishlist = e.target.closest('[data-pdp-wishlist]');
    if (wishlist) {
      const key = 'noire:wishlist';
      const handle = wishlist.dataset.productHandle;
      let items = [];
      try { items = JSON.parse(window.localStorage.getItem(key)) || []; } catch (error) {}
      const active = !items.includes(handle);
      items = active ? items.concat(handle) : items.filter((item) => item !== handle);
      try { window.localStorage.setItem(key, JSON.stringify(items)); } catch (error) {}
      wishlist.setAttribute('aria-pressed', active ? 'true' : 'false');
      wishlist.setAttribute('aria-label', (active ? 'Remove ' : 'Add ') + handle.replace(/-/g, ' ') + (active ? ' from wishlist' : ' to wishlist'));
      const label = wishlist.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
      return;
    }
    const writeReview = e.target.closest('[data-review-write]');
    if (writeReview) {
      const panel = writeReview.closest('.vs-review-panel');
      const target = panel && panel.querySelector('.vs-review-app-slot, .vs-review-items');
      if (target) target.scrollIntoView({ behavior: window.NoireUtils.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    const policyTrigger = e.target.closest('[data-policy-open]');
    if (policyTrigger) {
      const modal = document.getElementById(policyTrigger.dataset.policyOpen);
      if (modal && modal.open) modal.open(policyTrigger);
      return;
    }
    const aboutToggle = e.target.closest('[data-about-toggle]');
    if (aboutToggle) {
      const wrap = aboutToggle.closest('.product__about');
      const copy = wrap && wrap.querySelector('[data-about-copy]');
      if (!copy) return;
      const expanded = copy.classList.toggle('is-expanded');
      aboutToggle.textContent = expanded ? aboutToggle.dataset.lessLabel : aboutToggle.dataset.moreLabel;
      return;
    }
    const reviewQuestion = e.target.closest('[data-review-question]');
    if (reviewQuestion) {
      const root = reviewQuestion.closest('[data-product-root]');
      const detail = {
        productHandle: root ? root.dataset.productHandle : '',
        question: reviewQuestion.dataset.question || '',
        custom: reviewQuestion.dataset.questionMode === 'custom',
        trigger: reviewQuestion
      };
      window.dispatchEvent(new CustomEvent('nas:review-question', { detail }));
      const reviewsApp = document.querySelector('nas-reviews');
      const questionArea = reviewQuestion.closest('.vs-product-questions');
      let inlineAnswer = questionArea && questionArea.querySelector('[data-inline-ai-answer]');
      if (questionArea && !inlineAnswer) {
        inlineAnswer = document.createElement('div');
        inlineAnswer.className = 'vs-inline-ai-answer';
        inlineAnswer.dataset.inlineAiAnswer = '';
        inlineAnswer.setAttribute('role', 'status');
        questionArea.appendChild(inlineAnswer);
      }
      const submitQuestion = async (question) => {
        if (!reviewsApp || typeof reviewsApp.askQuestion !== 'function') {
          if (inlineAnswer) inlineAnswer.textContent = 'Questions are temporarily unavailable.';
          return;
        }
        if (inlineAnswer) inlineAnswer.textContent = 'Reading approved customer reviews…';
        try {
          const answer = await reviewsApp.askQuestion(question);
          if (inlineAnswer) inlineAnswer.textContent = answer;
        } catch (error) {
          if (inlineAnswer) inlineAnswer.textContent = error.message || 'Could not answer that right now.';
        }
      };
      if (!detail.custom && detail.question) {
        submitQuestion(detail.question);
      } else if (questionArea) {
        let inlineForm = questionArea.querySelector('[data-inline-ai-form]');
        if (!inlineForm) {
          inlineForm = document.createElement('form');
          inlineForm.className = 'vs-inline-ai-form';
          inlineForm.dataset.inlineAiForm = '';
          inlineForm.innerHTML = '<input name="question" maxlength="500" required placeholder="Ask about fit, fabric or quality"><button type="submit">Ask AI</button>';
          inlineForm.addEventListener('submit', (event) => {
            event.preventDefault();
            const question = new FormData(inlineForm).get('question');
            if (question) submitQuestion(question);
          });
          questionArea.appendChild(inlineForm);
        }
        inlineForm.querySelector('input')?.focus();
      }
      return;
    }
    const questionToggle = e.target.closest('[data-question-toggle]');
    if (questionToggle) {
      const wrap = questionToggle.closest('.vs-product-questions');
      const list = wrap && wrap.querySelector('.vs-question-list');
      if (list) list.hidden = !list.hidden;
      return;
    }
    const productQuestion = e.target.closest('[data-product-question]');
    if (productQuestion) {
      const question = productQuestion.closest('.vs-question');
      const answer = question && question.querySelector('[data-question-answer]');
      if (!answer) return;
      const expanded = answer.hidden;
      answer.hidden = !expanded;
      productQuestion.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const marker = productQuestion.querySelector('span');
      if (marker) marker.textContent = expanded ? '−' : '+';
      return;
    }
    const quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      const variantId = parseInt(quickAdd.dataset.variantId, 10);
      if (variantId) {
        quickAdd.classList.add('is-loading');
        window.NoireCart.addItems([{ id: variantId, quantity: 1 }])
          .then(() => {
            window.NoireUtils.announce(window.a11yStrings.itemAdded);
            window.NoireCart.openDrawer();
          })
          .catch(() => {})
          .finally(() => quickAdd.classList.remove('is-loading'));
      } else if (quickAdd.dataset.productUrl) {
        openQuickView(quickAdd.dataset.productUrl, quickAdd);
      }
      return;
    }
    const qv = e.target.closest('[data-quick-view]');
    if (qv && qv.dataset.productUrl) {
      e.preventDefault();
      openQuickView(qv.dataset.productUrl, qv);
    }
  });

  /* ---------- Gift recipient toggle ---------- */
  document.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-recipient-toggle]');
    if (!toggle) return;
    const wrap = toggle.closest('.gift-recipient') || toggle.closest('form') || document;
    const fields = wrap.querySelector('[data-recipient-fields]');
    if (fields) {
      fields.hidden = !toggle.checked;
      fields.querySelectorAll('input, textarea').forEach((f) => { f.disabled = !toggle.checked; });
      const offset = fields.querySelector('[data-recipient-offset]');
      if (offset && toggle.checked) offset.value = String(new Date().getTimezoneOffset());
    }
  });

  function initRecipientForms() {
    document.querySelectorAll('[data-recipient-toggle]').forEach((toggle) => {
      const wrap = toggle.closest('.gift-recipient') || toggle.closest('form') || document;
      const fields = wrap.querySelector('[data-recipient-fields]');
      if (!fields) return;
      fields.hidden = !toggle.checked;
      fields.querySelectorAll('input, textarea').forEach((f) => { f.disabled = !toggle.checked; });
    });
  }

  function initPdpWishlist() {
    let items = [];
    try { items = JSON.parse(window.localStorage.getItem('noire:wishlist')) || []; } catch (error) {}
    document.querySelectorAll('[data-pdp-wishlist]').forEach((button) => {
      const active = items.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
    });
  }

  function initReviewTools() {
    document.querySelectorAll('.vs-review-panel').forEach((panel) => {
      const items = Array.from(panel.querySelectorAll('.vs-review-item'));
      if (!items.length || panel.dataset.reviewToolsReady === 'true') return;
      panel.dataset.reviewToolsReady = 'true';
      const filters = Array.from(panel.querySelectorAll('[data-review-filter]'));
      filters.forEach((select) => {
        const field = select.dataset.reviewFilter;
        if (field === 'rating') return;
        const values = Array.from(new Set(items.map((item) => item.dataset['review' + field.charAt(0).toUpperCase() + field.slice(1)]).filter(Boolean))).sort();
        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });
      });
      const list = panel.querySelector('.vs-review-items');
      const update = () => {
        items.forEach((item) => {
          item.hidden = filters.some((select) => {
            if (!select.value) return false;
            const field = select.dataset.reviewFilter;
            const key = 'review' + field.charAt(0).toUpperCase() + field.slice(1);
            return String(item.dataset[key] || '') !== select.value;
          });
        });
        const sort = panel.querySelector('[data-review-sort]');
        if (!sort || !list) return;
        const ordered = items.slice().sort((a, b) => {
          if (sort.value === 'highest') return Number(b.dataset.reviewRating) - Number(a.dataset.reviewRating);
          if (sort.value === 'lowest') return Number(a.dataset.reviewRating) - Number(b.dataset.reviewRating);
          return Date.parse(b.dataset.reviewDate || 0) - Date.parse(a.dataset.reviewDate || 0);
        });
        ordered.forEach((item) => list.appendChild(item));
      };
      filters.forEach((select) => select.addEventListener('change', update));
      const sort = panel.querySelector('[data-review-sort]');
      if (sort) sort.addEventListener('change', update);
    });
  }

  function initModelViewers() {
    if (!document.querySelector('model-viewer')) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([
      { name: 'model-viewer-ui', version: '1.0' },
      { name: 'shopify-xr', version: '1.0' }
    ], (error) => {
      if (error) return;
      document.querySelectorAll('model-viewer').forEach((mv) => {
        if (!mv.dataset.initialized && window.Shopify.ModelViewerUI) {
          new window.Shopify.ModelViewerUI(mv);
          mv.dataset.initialized = 'true';
        }
      });
    });
  }

  /* ---------- Share ---------- */
  document.addEventListener('click', (e) => {
    const share = e.target.closest('[data-share-button]');
    if (!share) return;
    e.preventDefault();
    const url = share.dataset.shareUrl || window.location.href;
    const title = share.dataset.shareTitle || document.title;
    if (navigator.share) {
      navigator.share({ url, title }).catch(() => {});
      return;
    }
    const write = navigator.clipboard && navigator.clipboard.writeText
      ? navigator.clipboard.writeText(url)
      : Promise.reject();
    write.then(() => {
      const msg = share.querySelector('[data-share-label]');
      if (msg) {
        const original = msg.textContent;
        msg.textContent = share.dataset.copiedText || original;
        window.setTimeout(() => { msg.textContent = original; }, 2000);
      }
      window.NoireUtils.announce(share.dataset.copiedText || '');
    }).catch(() => {
      window.prompt('URL', url);
    });
  });

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'noire:recent';
  function readRecent() {
    try { return JSON.parse(window.localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
  }
  function storeRecent() {
    const rootEl = document.querySelector('[data-main-product][data-product-handle]');
    if (!rootEl) return;
    const handle = rootEl.dataset.productHandle;
    if (!handle) return;
    try {
      let list = readRecent().filter((h) => h !== handle);
      list.unshift(handle);
      list = list.slice(0, 12);
      window.localStorage.setItem(RV_KEY, JSON.stringify(list));
    } catch (e) { /* storage unavailable */ }
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const grid = this.querySelector('[data-rv-grid]');
      const fallback = this.querySelector('[data-rv-fallback]');
      if (!grid) return;
      const exclude = this.dataset.exclude;
      const limit = parseInt(this.dataset.limit || '4', 10);
      const cardSection = this.dataset.cardSection || 'nas-recent-product-card';
      const handles = readRecent().filter((h) => h !== exclude).slice(0, limit);
      if (!handles.length) return;
      Promise.all(handles.map((h) =>
        fetch('/products/' + encodeURIComponent(h) + '?section_id=' + encodeURIComponent(cardSection))
          .then((r) => (r.ok ? r.text() : null))
          .catch(() => null)
      )).then((responses) => {
        responses.filter(Boolean).forEach((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('[data-rv-card] > .card');
          if (card) grid.appendChild(card);
        });
        if (!grid.children.length) return;
        grid.hidden = false;
        if (fallback) fallback.hidden = true;
        initCardReviewSummaries(grid);
      });
    }
  }
  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      const grid = this.querySelector('[data-recommendations]');
      if (!grid) return;
      const limit = parseInt(this.dataset.limit || '8', 10);
      const cardSection = this.dataset.cardSection || 'nas-cart-recommendation-card';
      const cartHandles = new Set((this.dataset.cartHandles || '').split(',').filter(Boolean));
      const recentHandles = readRecent().filter((handle, index, list) => (
        handle && !cartHandles.has(handle) && list.indexOf(handle) === index
      )).slice(0, limit);
      const bestsellers = Array.from(grid.querySelectorAll('[data-cart-recommendation-card]'));

      const render = (recentCards) => {
        const seen = new Set(cartHandles);
        const ordered = [];
        recentCards.forEach((card) => {
          const handle = card?.dataset.productHandle;
          if (!handle || seen.has(handle)) return;
          seen.add(handle);
          card.dataset.recommendationSource = 'recently-viewed';
          ordered.push(card);
        });
        bestsellers.forEach((card) => {
          const handle = card.dataset.productHandle;
          if (!handle || seen.has(handle) || ordered.length >= limit) return;
          seen.add(handle);
          ordered.push(card);
        });
        grid.replaceChildren(...ordered.slice(0, limit));
        grid.scrollLeft = 0;
        window.requestAnimationFrame(() => { grid.scrollLeft = 0; });
        this.closest('[data-recommendations-wrap]')?.toggleAttribute('hidden', !grid.children.length);
        initCardReviewSummaries(grid);
      };

      if (!recentHandles.length) {
        Promise.resolve().then(() => render([]));
        return;
      }
      Promise.all(recentHandles.map((handle) =>
        fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(cardSection))
          .then((response) => (response.ok ? response.text() : null))
          .then((html) => {
            if (!html) return null;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.querySelector('[data-cart-recommendation-response] > [data-cart-recommendation-card]');
          })
          .catch(() => null)
      )).then(render);
    }
  }
  if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('[data-recommendations]');
            const wrap = this.closest('[data-recommendations-wrap]');
            if (fresh && fresh.children.length) {
              const current = this.querySelector('[data-recommendations]');
              if (current) {
                current.innerHTML = fresh.innerHTML;
                initCardReviewSummaries(current);
              }
            } else if (wrap) {
              wrap.hidden = true;
            }
          })
          .catch(() => {});
      };
      if (this.closest('.nas-cart-drawer__recommendations')) {
        load();
      } else if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((en) => en.isIntersecting)) {
            io.disconnect();
            load();
          }
        }, { rootMargin: '300px 0px' });
        io.observe(this);
      } else {
        load();
      }
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- PDP recommendation rails, card swatches and review gate ---------- */
  const nasCardReviewSummaries = new Map();

  function loadCardReviewSummary(handle) {
    if (!nasCardReviewSummaries.has(handle)) {
      nasCardReviewSummaries.set(handle,
        fetch(`https://nivaelle-reviews.rosi-platform.workers.dev/api/products/${encodeURIComponent(handle)}/reviews?limit=1`)
          .then((response) => {
            if (!response.ok) throw new Error(`Card review summary request failed: ${response.status}`);
            return response.json();
          })
          .then((payload) => ({
            count: Number(payload?.summary?.count) || 0,
            average: Number(payload?.summary?.average) || 0
          }))
          .catch(() => ({ count: 0, average: 0 }))
      );
    }
    return nasCardReviewSummaries.get(handle);
  }

  function initCardReviewSummaries(root = document) {
    root.querySelectorAll('[data-nas-card-rating]').forEach((rating) => {
      if (rating.dataset.nasReviewBound === 'true') return;
      rating.dataset.nasReviewBound = 'true';
      const handle = rating.dataset.productHandle;
      if (!handle) return;
      loadCardReviewSummary(handle).then(({ count, average }) => {
        if (!count) return;
        const fill = rating.querySelector('[data-nas-card-rating-fill]');
        const countEl = rating.querySelector('[data-nas-card-rating-count]');
        if (fill) fill.style.setProperty('--nas-card-rating-width', `${Math.max(0, Math.min(100, average / 5 * 100))}%`);
        if (countEl) countEl.textContent = `(${count})`;
        rating.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars from ${count} ${count === 1 ? 'review' : 'reviews'}`);
        rating.hidden = false;
      });
    });
  }
  const cardVariantMediaIndexes = {"aaliyah-lingerie":{"Black":0},"amber-lingerie-set":{"Black":0,"Blue":20,"Dark Green":7,"Red":13},"amelias-nightdress":{"White":0,"Black":7},"amelias-stockings":{"Black":0},"amour-thong":{"Black":0,"White":4},"angelique-bodystocking":{"Black":0},"arabesque-thong":{"White":0,"Black":4},"aria-lingerie-set":{"Green":0,"White":7,"Black":12},"aurelia-lace-tanga":{"White":0,"Black":4,"Red":8},"aurora-satin-lace-lounge-set-with-kimono-shorts":{"Blush":2,"Lavender":8,"Black":12,"Green":21,"Navy Blue":26},"avas-bodystocking":{"Black":0},"camille-skirt-set":{"Black":0,"Red":6,"White":14,"Pink":22},"carmen":{"Black":0,"White":9,"Red":14,"Green":21,"Blue":28},"celeste-bodysuit":{"Black":0},"celeste-cross-thong":{"Black":0,"White":4,"Red":9},"charlotte-lingerie-set":{"Black":0},"clara-lingerie-set":{"Red":0,"Black":5},"colette-2-piece-lingerie-set":{"White":0,"Black":6,"Blue":12,"Red":20},"cynthia-lingerie-set":{"Brown":0,"Black":6,"Red":14,"Green":17},"daisy-lingerie-set":{"Blue":0,"Black":6,"Yellow":11,"Green":13},"elena-lingerie-set":{"Black":0},"ella-lingerie-set":{"Black":0},"elvira-slip-set":{"Pink":2,"Red":8,"Black":12,"White":18,"Navy Blue":23,"Champagne":28},"envy-lingerie-set":{"Black":0,"Red":9},"evas-nightdress":{"Black":0,"White":7},"fleur-thong":{"Black":0,"White":5},"front-close-intimates-set":{"Black":3},"gia-bodysuit":{"Black":0},"gina-sheer-thong":{"Black":0,"White":4,"Red":8},"grace-intimates-set":{"Black":0},"iris-4-piece-lingerie-set":{"Sky Blue":0,"Red":6,"Black":13},"iris-embroidered-garter-set":{"Green":0},"isabella-bodysuit":{"Black":0,"Baby Pink":5,"Red":33,"Burgundy Red":15,"Purple":29,"Navy Blue":20,"Royal Blue":10,"White":39},"isabella-lingerie-set":{"Pink":0},"jacey-lace-bodysuit":{"Red":0,"Black":7,"White":14},"jade-3-piece-lingerie-set":{"Black":0},"jessica-2-piece-lingerie-set":{"Leopard":0},"jordan-lingerie-set":{"Black":0},"joyce-4-piece-set":{"Black":0},"julietta-french":{"Black":0,"White":7,"Red":13},"kristinas-bodysuit":{"Black":0,"Red":7},"lace-kimono":{"Black":0,"White":4},"laurel-4-piece-set":{"Blue":0,"Red":4,"Brown":9,"Black":14},"lia-floral-nightgown":{"White (almost sold out)":0,"Black":6,"Light Blue":12,"Yellow":17,"Blue":22,"Pink":28},"liana-lingerie-set":{"Black":0,"Red":8,"White":19,"Ruby Noir":25},"lila-bodysuit":{"Black":0,"Blue":7,"Red":13,"White":20},"lilia-corset-set":{"Purple":0},"lisa-lingerie":{"White":0,"Black":6,"Turquoise":11,"Burgundy Red":15,"Blue":22},"long-lace-gloves":{"Black":0,"White":4},"lorin-bodysuit":{"Navy Blue":0,"Red":7,"White":14,"Black":21},"lucia-chemise-set":{"Navy Blue":0,"Black":5},"lucy-lingerie-set":{"Red":0,"White":7,"Pink":16,"Brown":24,"Black":26},"lunas-lingerie-set":{"White":0,"Purple":5,"Green":10,"Blue":15,"Red":20},"lunas-secret":{"Red":0},"magda-3-piece-set":{"White":0},"magda-lingerie":{"White":0,"Black":8,"Blue":16,"Orange":24},"maja-lingerie-set":{"White":0,"Black":7},"melissa-bodystocking":{"White":0,"Red":4,"Black":8},"nadia-lingerie-set":{"Black":0,"Red":6,"White":15},"noa-loungewear":{"Red":0,"Green":6,"Pink":14,"Black":17,"Navy Blue":22},"nora-lingerie-set":{"Black":0},"nova-nightgown":{"Black":0,"White":7,"Red":14,"Blue":21,"Navy Blue":28},"pernille-ensemble":{"Black":0},"selene-bodysuit":{"Pink":0,"Blue":7,"Violet":14,"Grey":21},"shira":{"Blue":0,"White":7,"Black":9},"sofia-lingerie-set-5-pieces":{"Black":0,"Red":7,"White":14},"stella-lingerie-set":{"Black":0},"tyras-bodystocking":{"Black":0},"valeria-lingerie-set-1":{"Black":0},"valntina-brief":{"Black":0,"Red":5,"White":9},"velora-g-string":{"Red":0,"Black":3,"Beige":2},"victoria-lingerie-set":{"Green":0},"violet-lingerie-set":{"Champagne":0},"violy-bodysuit":{"White":0},"zephyra-lingerie-set":{"Black":0}};
  const cardProductRequests = new Map();

  function cardProductData(handle) {
    if (!cardProductRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      cardProductRequests.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      }));
    }
    return cardProductRequests.get(handle);
  }

  function colourMedia(productData, handle, colour, limit) {
    const indexMap = cardVariantMediaIndexes[handle];
    if (!indexMap || !colour || !Array.isArray(productData?.media)) return [];
    const mappedColour = Object.keys(indexMap).find((name) => name.toLowerCase() === String(colour).toLowerCase());
    if (!mappedColour) return [];
    const start = indexMap[mappedColour];
    if (!Number.isInteger(start)) return [];
    const next = Object.values(indexMap).filter((index) => Number.isInteger(index) && index > start).sort((a, b) => a - b)[0];
    return productData.media.slice(start, next || productData.media.length).slice(0, limit || 3);
  }

  function imageUrlForMedia(media, width) {
    const url = new URL(media.src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.href;
  }

  function updateCartQuickViewImages(root, colour) {
    const handle = root?.dataset.productHandle;
    const images = Array.from(root?.closest('.nas-cart-quick-view')?.querySelectorAll('.nas-cart-quick-view__media img') || []);
    if (!handle || !images.length) return;
    cardProductData(handle).then((productData) => {
      const mediaItems = colourMedia(productData, handle, colour, images.length);
      if (!mediaItems.length) return;
      images.forEach((image, index) => {
        const media = mediaItems[index];
        image.closest('.media').hidden = !media;
        if (!media) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = imageUrlForMedia(media, 560);
        image.alt = media.alt || `${productData.title} in ${colour}`;
      });
    }).catch(() => {});
  }

  function syncCartLineImages(scope) {
    (scope || document).querySelectorAll('[data-cart-product-handle][data-cart-colour]').forEach((line) => {
      const handle = line.dataset.cartProductHandle;
      const colour = line.dataset.cartColour;
      const image = line.querySelector('[data-cart-line-image]');
      if (!handle || !colour || !image) return;
      cardProductData(handle).then((productData) => {
        const media = colourMedia(productData, handle, colour, 1)[0];
        if (!media) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = imageUrlForMedia(media, 360);
        image.alt = media.alt || `${productData.title} in ${colour}`;
      }).catch(() => {});
    });
  }

  async function selectCardMaterialSwatch(button) {
    const card = button.closest('.card');
    const handle = button.dataset.productHandle;
    const label = button.dataset.swatchLabel;
    const mediaId = String(button.dataset.mediaId || '');
    const mediaIndex = cardVariantMediaIndexes[handle]?.[label];
    if (!card || !handle || !label || (!mediaId && !Number.isInteger(mediaIndex))) return;

    const selectionToken = `${handle}-${label}-${Date.now()}`;
    card.dataset.swatchSelection = selectionToken;
    card.setAttribute('aria-busy', 'true');
    try {
      const productData = await cardProductData(handle);
      if (card.dataset.swatchSelection !== selectionToken) return;
      const media = productData.media?.find((item) => String(item.id) === mediaId) || productData.media?.[mediaIndex];
      const image = card.querySelector('.card__media .media img:not(.card__img--secondary)');
      if (!media?.src || !image) return;
      const imageUrl = new URL(media.src, window.location.origin);
      imageUrl.searchParams.set('width', '900');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = imageUrl.href;
      image.alt = media.alt || `${productData.title} in ${label}`;
      card.classList.add('card--swatch-selected');
      card.querySelectorAll('[data-card-material-swatch]').forEach((swatch) => {
        swatch.setAttribute('aria-pressed', String(swatch === button));
      });
    } catch (_) {
      /* Keep the existing card image and state when product data is unavailable. */
    } finally {
      if (card.dataset.swatchSelection === selectionToken) card.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-card-material-swatch]');
    if (button) selectCardMaterialSwatch(button);
  });

  function initPdpRails(scope) {
    (scope || document).querySelectorAll('.nas-pdp-lane').forEach((lane) => {
      if (lane.dataset.nasRailReady === 'true') return;
      lane.dataset.nasRailReady = 'true';
      const previous = lane.querySelector('[data-nas-carousel-previous]');
      const next = lane.querySelector('[data-nas-carousel-next]');
      const activeRail = () => Array.from(lane.querySelectorAll('[data-nas-carousel]')).find((rail) => !rail.hidden);
      const update = () => {
        const rail = activeRail();
        if (!rail) return;
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        if (previous) previous.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max - 2;
      };
      const step = (direction) => {
        const rail = activeRail();
        if (!rail) return;
        const card = rail.querySelector('.card');
        const gap = parseFloat(window.getComputedStyle(rail).columnGap || window.getComputedStyle(rail).gap) || 0;
        rail.scrollBy({ left: direction * ((card && card.getBoundingClientRect().width) || rail.clientWidth) + direction * gap, behavior: 'smooth' });
      };
      previous?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      lane.addEventListener('scroll', update, true);
      new MutationObserver(update).observe(lane, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(lane);
      update();
    });
  }

  function initProductionReviewGates(scope) {
    (scope || document).querySelectorAll('[data-nas-reviews-gate]').forEach((gate) => {
      if (gate.dataset.nasReviewGateReady === 'true') return;
      gate.dataset.nasReviewGateReady = 'true';
      const host = gate.querySelector('nas-reviews');
      const sync = () => { gate.hidden = !host || host.dataset.state !== 'ready'; };
      sync();
      if (host) new MutationObserver(sync).observe(host, { attributes: true, attributeFilter: ['data-state'] });
    });
  }

  function initReviewSummary(scope) {
    const root = (scope || document).querySelector('[data-main-product]');
    const handle = root?.dataset.productHandle;
    if (!handle || root.dataset.nasReviewSummaryReady === 'true') return;
    root.dataset.nasReviewSummaryReady = 'true';
    fetch(`https://nivaelle-reviews.rosi-platform.workers.dev/api/products/${encodeURIComponent(handle)}/reviews?limit=1`)
      .then((response) => {
        if (!response.ok) throw new Error(`Review summary request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        const count = Number(payload?.summary?.count) || 0;
        const average = Number(payload?.summary?.average) || 0;
        document.querySelectorAll('[data-nas-rating-summary]').forEach((summary) => {
          summary.querySelector('[data-nas-rating-average]').textContent = average.toFixed(1);
          summary.querySelector('[data-nas-rating-count]').textContent = `${count} ${count === 1 ? 'review' : 'reviews'}`;
          const stars = summary.querySelector('.vs-stars');
          if (stars) stars.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);
          summary.hidden = count === 0;
        });
        document.querySelectorAll('[data-nas-fit-summary]').forEach((summary) => {
          summary.querySelector('[data-nas-fit-count]').textContent = `${count} ${count === 1 ? 'Review' : 'Reviews'}`;
          summary.hidden = count === 0;
        });
      })
      .catch((error) => console.warn('[NAS reviews]', error));
  }

  function initQuestionCarousels(scope) {
    (scope || document).querySelectorAll('[data-question-carousel-wrap]').forEach((wrap) => {
      if (wrap.dataset.questionCarouselReady === 'true') return;
      wrap.dataset.questionCarouselReady = 'true';
      const rail = wrap.querySelector('[data-question-carousel]');
      const previous = wrap.querySelector('[data-question-carousel-previous]');
      const next = wrap.querySelector('[data-question-carousel-next]');
      if (!rail) return;
      const update = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        if (previous) previous.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max - 2;
      };
      const step = (direction) => rail.scrollBy({ left: direction * Math.max(180, rail.clientWidth * .72), behavior: 'smooth' });
      previous?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      rail.addEventListener('scroll', update, { passive: true });
      let dragStartX = 0;
      let dragStartScroll = 0;
      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.target.closest('button, a, input')) return;
        dragStartX = event.clientX;
        dragStartScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      });
      rail.addEventListener('pointermove', (event) => {
        if (!rail.classList.contains('is-dragging')) return;
        rail.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
      });
      const stopDrag = (event) => {
        rail.classList.remove('is-dragging');
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      };
      rail.addEventListener('pointerup', stopDrag);
      rail.addEventListener('pointercancel', stopDrag);
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(rail);
      update();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    storeRecent();
    initRecipientForms();
    initPdpWishlist();
    initReviewTools();
    initModelViewers();
    initPdpRails(document);
    initCardReviewSummaries(document);
    initProductionReviewGates(document);
    initReviewSummary(document);
    initQuestionCarousels(document);
    syncCartLineImages(document);
  });
  document.addEventListener('cart:update', () => syncCartLineImages(document));
  document.addEventListener('shopify:section:load', () => {
    initRecipientForms();
    initPdpWishlist();
    initReviewTools();
    initModelViewers();
    initPdpRails(document);
    initCardReviewSummaries(document);
    initProductionReviewGates(document);
    initReviewSummary(document);
    initQuestionCarousels(document);
  });
})();
